import OpenOrderDetailsHeader from "@dana/Components/OpenOrderDetails/OpenOrderDetailsHeader";
import OpenOrderDetailsTable from "@dana/Components/OpenOrderDetails/OpenOrderDetailsTable";
import VectorIcon from "@dana/Icons/VectorIcon";
import { BackorderResponse } from "@dana/Types/ApiModels";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import Search from "@insite/mobius/Icons/Search";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useState } from "react";
import { css } from "styled-components";

// --- Props Interface ---
interface OpenOrderSearchProps {
    openOrders: BackorderResponse;
}

// --- Styles Interfaces and Objects ---
export interface OpenOrderSearchStyles {
    wrapper?: InjectableCss;
    innerWrapper?: InjectableCss;
    gridContainer?: GridContainerProps;
    gridItem?: GridItemProps;
    searchInputWrap?: InjectableCss;
    searchTitle?: TypographyPresentationProps;
    input?: TextFieldPresentationProps;
    mainNavigationRowIcon?: LinkPresentationProps;
}

export const OpenOrderSearchStyles: OpenOrderSearchStyles = {
    wrapper: {
        css: css`
            width: 100%;
        `,
    },
    innerWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            margin-bottom: 16px;
            align-items: center;
        `
    },
    searchTitle: {
        variant: "h5",
        css: css`
            margin-bottom: 0;
        `,
    },
    gridContainer: {
        gap: 12,
        css: css`
            margin-bottom: 32px;
        `,
    },
    gridItem: {
        width: [12, 6, 6, 3, 3],
    },
    searchInputWrap: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    input: {
        iconProps: { src: Search },
        mergeCss: true,
    },
    mainNavigationRowIcon: {
        css: css`
            position: absolute;
            right: 40px;
            top: 50%;
            transform: translateY(-50%);
        `,
    },
};

const styles = OpenOrderSearchStyles;

type Props = OpenOrderSearchProps;
// --- Component Definition ---
const OpenOrderSearch: FC<Props> = ({ openOrders }) => {
    const [partNoQuery, setPartNoSearchQuery] = React.useState("");
    const [orderNoQuery, setOrderNoSearchQuery] = React.useState("");
    const [primaryPoQuery, setPrimaryPoSearchQuery] = React.useState("");
    const [secondaryPoQuery, setSecondaryPoSearchQuery] = React.useState("");
    const [filteredData, setFilteredData] = useState(openOrders);

    let result = openOrders?.backorders;

    useEffect(() => {
        setFilteredData(openOrders);
    }, [openOrders]);

    const searchFilter = (name: string, value: string) => {
        const tempFilters = {
            partNoQuery,
            orderNoQuery,
            primaryPoQuery,
            secondaryPoQuery,
        };

        if (name === "PO") {
            tempFilters.primaryPoQuery = value;
        }
        if (name === "SPO") {
            tempFilters.secondaryPoQuery = value;
        }
        if (name === "ON") {
            tempFilters.orderNoQuery = value;
        }
        if (name === "PN") {
            tempFilters.partNoQuery = value;
        }

        setPrimaryPoSearchQuery(tempFilters.primaryPoQuery);
        setSecondaryPoSearchQuery(tempFilters.secondaryPoQuery);
        setOrderNoSearchQuery(tempFilters.orderNoQuery);
        setPartNoSearchQuery(tempFilters.partNoQuery);

        const result = openOrders?.backorders?.filter(item => {
            return (
                (!tempFilters.orderNoQuery ||
                    item.salesOrderNumber?.toLowerCase().includes(tempFilters.orderNoQuery.toLowerCase())) &&
                (!tempFilters.partNoQuery ||
                    item.partNumber?.toLowerCase().includes(tempFilters.partNoQuery.toLowerCase())) &&
                (!tempFilters.primaryPoQuery ||
                    item.poNumber?.toLowerCase().includes(tempFilters.primaryPoQuery.toLowerCase())) &&
                (!tempFilters.secondaryPoQuery ||
                    item.secondryPO?.toLowerCase().includes(tempFilters.secondaryPoQuery.toLowerCase()))
            );
        });

        if (result) {
            const filteredOrders = {
                ...openOrders,
                backorders: result,
            };
            setFilteredData(filteredOrders);
        }
    };

    const clearSearch = (name: string) => {
        const tempFilters = {
            partNoQuery,
            orderNoQuery,
            primaryPoQuery,
            secondaryPoQuery,
        };

        if (name === "PO") {
            tempFilters.primaryPoQuery = "";
        }
        if (name === "SPO") {
            tempFilters.secondaryPoQuery = "";
        }
        if (name === "ON") {
            tempFilters.orderNoQuery = "";
        }
        if (name === "PN") {
            tempFilters.partNoQuery = "";
        }

        setPrimaryPoSearchQuery(tempFilters.primaryPoQuery);
        setSecondaryPoSearchQuery(tempFilters.secondaryPoQuery);
        setOrderNoSearchQuery(tempFilters.orderNoQuery);
        setPartNoSearchQuery(tempFilters.partNoQuery);

        const result = openOrders?.backorders?.filter(item => {
            return (
                (!tempFilters.orderNoQuery ||
                    item.salesOrderNumber?.toLowerCase().includes(tempFilters.orderNoQuery.toLowerCase())) &&
                (!tempFilters.partNoQuery ||
                    item.partNumber?.toLowerCase().includes(tempFilters.partNoQuery.toLowerCase())) &&
                (!tempFilters.primaryPoQuery ||
                    item.poNumber?.toLowerCase().includes(tempFilters.primaryPoQuery.toLowerCase())) &&
                (!tempFilters.secondaryPoQuery ||
                    item.secondryPO?.toLowerCase().includes(tempFilters.secondaryPoQuery.toLowerCase()))
            );
        });

        if (result) {
            const filteredOrders = {
                ...openOrders,
                backorders: result,
            };
            setFilteredData(filteredOrders);
        }
    };


    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.innerWrapper}>
                <Typography {...styles.searchTitle}>{translate("Search By")}</Typography>
                <OpenOrderDetailsHeader openOrders={filteredData}></OpenOrderDetailsHeader>
            </StyledWrapper>
            <GridContainer {...styles.gridContainer}>
                <GridItem {...styles.gridItem}>
                    <StyledWrapper {...styles.searchInputWrap}>
                        <TextField
                            {...styles.input}
                            value={partNoQuery}
                            onChange={e => searchFilter("PN", e.target.value)}
                            placeholder={translate("Search Part No")}
                            autoComplete="off"
                            clickableText={translate("submit search")}
                            data-test-selector="open_order_part_serach"
                            iconClickableProps={{}}
                        />
                        {partNoQuery && (
                            <Link
                                onClick={e => {
                                    e.preventDefault();
                                }}
                                onKeyDown={e => {
                                    if (e.key === "Enter") {
                                        clearSearch("PN");
                                    }
                                }}
                                {...styles.mainNavigationRowIcon}
                            >
                                <Icon
                                    src={VectorIcon}
                                    size={18}
                                    className="clear-icon"
                                    onClick={() => clearSearch("PN")}
                                />
                            </Link>
                        )}
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.gridItem}>
                    <StyledWrapper {...styles.searchInputWrap}>
                        <TextField
                            {...styles.input}
                            value={orderNoQuery}
                            onChange={e => searchFilter("ON", e.target.value)}
                            placeholder={translate("Search Order No.")}
                            autoComplete="off"
                            clickableText={translate("submit search")}
                            data-test-selector="open_order_no_search"
                            iconClickableProps={{}}
                        />
                        {orderNoQuery && (
                            <Link
                                onClick={e => {
                                    e.preventDefault();
                                }}
                                onKeyDown={e => {
                                    if (e.key === "Enter") {
                                        clearSearch("ON");
                                    }
                                }}
                                {...styles.mainNavigationRowIcon}
                            >
                                <Icon
                                    src={VectorIcon}
                                    size={18}
                                    className="clear-icon"
                                    onClick={() => clearSearch("ON")}
                                />
                            </Link>
                        )}
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.gridItem}>
                    <StyledWrapper {...styles.searchInputWrap}>
                        <TextField
                            {...styles.input}
                            value={primaryPoQuery}
                            onChange={e => searchFilter("PO", e.target.value)}
                            placeholder={translate("Search Primary PO")}
                            autoComplete="off"
                            clickableText={translate("submit search")}
                            data-test-selector="open_order_primary_po_search"
                            iconClickableProps={{}}
                        />
                        {primaryPoQuery && (
                            <Link
                                onClick={e => {
                                    e.preventDefault();
                                }}
                                onKeyDown={e => {
                                    if (e.key === "Enter") {
                                        clearSearch("PO");
                                    }
                                }}
                                {...styles.mainNavigationRowIcon}
                            >
                                <Icon
                                    src={VectorIcon}
                                    size={18}
                                    className="clear-icon"
                                    onClick={() => clearSearch("PO")}
                                />
                            </Link>
                        )}
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.gridItem}>
                    <StyledWrapper {...styles.searchInputWrap}>
                        <TextField
                            {...styles.input}
                            value={secondaryPoQuery}
                            onChange={e => searchFilter("SPO", e.target.value)}
                            placeholder={translate("Search Secondary PO")}
                            autoComplete="off"
                            clickableText={translate("submit search")}
                            data-test-selector="open_order_secondary_po_search"
                            iconClickableProps={{}}
                        />
                        {secondaryPoQuery && (
                            <Link
                                onClick={e => {
                                    e.preventDefault();
                                }}
                                onKeyDown={e => {
                                    if (e.key === "Enter") {
                                        clearSearch("SPO");
                                    }
                                }}
                                {...styles.mainNavigationRowIcon}
                            >
                                <Icon
                                    src={VectorIcon}
                                    size={18}
                                    className="clear-icon"
                                    onClick={() => clearSearch("SPO")}
                                />
                            </Link>
                        )}
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.gridItem}>

                </GridItem>
            </GridContainer>
           
            <OpenOrderDetailsTable openOrders={filteredData}></OpenOrderDetailsTable>
        </StyledWrapper>
    );
};

export default OpenOrderSearch;
